import { useState, useEffect } from 'react';
import { API, formatWeight, formatDateTime } from '@/lib/api';
import { useAuth } from '@/contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { Plus, Search, Eye, Pencil, Trash2 } from 'lucide-react';

export default function SuppliersPage() {
  const { user } = useAuth();
  const isAdmin = ['admin', 'analyst'].includes(user?.role);
  const [suppliers, setSuppliers] = useState([]);
  const [search, setSearch] = useState('');
  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm] = useState({ name: '', contact: '', address: '' });
  const [selectedSupplier, setSelectedSupplier] = useState(null);
  const [editSupplier, setEditSupplier] = useState(null);
  const [editForm, setEditForm] = useState({ name: '', contact: '', address: '' });

  const fetchSuppliers = async () => {
    try {
      const params = search ? { search } : {};
      setSuppliers((await API.get('/suppliers', { params })).data);
    } catch(e) { console.error(e); }
  };

  useEffect(() => { fetchSuppliers(); }, [search]);

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) { toast.error('Name is required'); return; }
    try {
      await API.post('/suppliers', form);
      toast.success('Karigar created'); setShowCreate(false);
      setForm({ name: '', contact: '', address: '' }); fetchSuppliers();
    } catch(err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  const viewDetail = async (supplier) => {
    try { setSelectedSupplier((await API.get(`/suppliers/${supplier.id}`)).data); }
    catch(e) { setSelectedSupplier(supplier); }
  };

  const openEdit = (supplier) => {
    setEditForm({ name: supplier.name || '', contact: supplier.contact || '', address: supplier.address || '' });
    setEditSupplier(supplier);
    setSelectedSupplier(null);
  };

  const handleEditSave = async () => {
    if (!editSupplier) return;
    try {
      await API.put(`/suppliers/${editSupplier.id}`, editForm);
      toast.success('Karigar updated'); setEditSupplier(null); fetchSuppliers();
    } catch(err) { toast.error(err.response?.data?.detail || 'Update failed'); }
  };

  const handleDelete = async (supplier) => {
    if (!window.confirm(`Delete karigar "${supplier.name}"? This cannot be undone.`)) return;
    try {
      await API.delete(`/suppliers/${supplier.id}`);
      toast.success('Karigar deleted'); fetchSuppliers();
    } catch(err) { toast.error(err.response?.data?.detail || 'Delete failed'); }
  };

  return (
    <div data-testid="karigars-page">
      <div className="page-header flex flex-col sm:flex-row sm:items-center justify-between gap-2">
        <div><h1>Karigars</h1><p className="hidden sm:block">Manage karigars and purchases</p></div>
        <Dialog open={showCreate} onOpenChange={setShowCreate}>
          <DialogTrigger asChild><Button className="w-full sm:w-auto" data-testid="new-karigar-btn"><Plus size={16} className="mr-2" />New Karigar</Button></DialogTrigger>
          <DialogContent className="max-h-[90vh] overflow-y-auto">
            <DialogHeader><DialogTitle>Create Karigar</DialogTitle></DialogHeader>
            <form onSubmit={handleCreate} className="space-y-4">
              <div><label className="text-sm font-medium mb-1 block">Name *</label><Input data-testid="karigar-create-name" value={form.name} onChange={e => setForm({...form, name: e.target.value})} required /></div>
              <div><label className="text-sm font-medium mb-1 block">Contact</label><Input value={form.contact} onChange={e => setForm({...form, contact: e.target.value})} /></div>
              <div><label className="text-sm font-medium mb-1 block">Address</label><Input value={form.address} onChange={e => setForm({...form, address: e.target.value})} /></div>
              <Button type="submit" className="w-full" data-testid="karigar-create-submit">Create</Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <div className="relative mb-4">
        <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
        <Input className="pl-9" placeholder="Search karigars..." value={search} onChange={e => setSearch(e.target.value)} data-testid="karigar-search" />
      </div>

      {/* Mobile */}
      <div className="mobile-card-list">
        {suppliers.map(s => (
          <Card key={s.id} data-testid={`karigar-card-${s.id}`}>
            <CardContent className="p-3">
              <div className="flex items-center justify-between">
                <div className="cursor-pointer flex-1" onClick={() => viewDetail(s)}>
                  <p className="font-semibold text-sm">{s.name}</p>
                  <p className="text-xs text-muted-foreground">{s.contact || '-'} | {s.address || '-'}</p>
                </div>
                <div className="flex gap-1">
                  <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => viewDetail(s)}><Eye size={14} /></Button>
                  {isAdmin && (
                    <>
                      <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => openEdit(s)} data-testid={`karigar-edit-${s.id}`}><Pencil size={14} /></Button>
                      <Button variant="ghost" size="icon" className="h-8 w-8 text-destructive" onClick={() => handleDelete(s)} data-testid={`karigar-delete-${s.id}`}><Trash2 size={14} /></Button>
                    </>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
        {suppliers.length === 0 && <p className="text-center py-8 text-sm text-muted-foreground">No karigars</p>}
      </div>

      {/* Desktop */}
      <div className="desktop-table">
        <Card><CardContent className="p-0"><div className="table-scroll-wrapper"><Table className="data-table">
          <TableHeader><TableRow>
            <TableHead>Name</TableHead><TableHead>Contact</TableHead><TableHead>Address</TableHead><TableHead>Since</TableHead><TableHead>Actions</TableHead>
          </TableRow></TableHeader>
          <TableBody>
            {suppliers.map(s => (
              <TableRow key={s.id}>
                <TableCell className="font-semibold">{s.name}</TableCell>
                <TableCell className="font-mono">{s.contact || '-'}</TableCell>
                <TableCell className="max-w-xs truncate">{s.address || '-'}</TableCell>
                <TableCell className="text-sm">{formatDateTime(s.created_at)}</TableCell>
                <TableCell>
                  <div className="flex gap-1">
                    <Button variant="ghost" size="icon" onClick={() => viewDetail(s)}><Eye size={16} /></Button>
                    {isAdmin && (
                      <>
                        <Button variant="ghost" size="icon" onClick={() => openEdit(s)} data-testid={`karigar-edit-desk-${s.id}`}><Pencil size={14} /></Button>
                        <Button variant="ghost" size="icon" className="text-destructive" onClick={() => handleDelete(s)} data-testid={`karigar-delete-desk-${s.id}`}><Trash2 size={14} /></Button>
                      </>
                    )}
                  </div>
                </TableCell>
              </TableRow>
            ))}
            {suppliers.length === 0 && <TableRow><TableCell colSpan={5} className="text-center py-8 text-muted-foreground">No karigars</TableCell></TableRow>}
          </TableBody>
        </Table></div></CardContent></Card>
      </div>

      {/* View Detail Dialog */}
      <Dialog open={!!selectedSupplier} onOpenChange={() => setSelectedSupplier(null)}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{selectedSupplier?.name}</DialogTitle></DialogHeader>
          {selectedSupplier && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div><span className="text-muted-foreground">Contact:</span><br />{selectedSupplier.contact || '-'}</div>
                <div><span className="text-muted-foreground">Address:</span><br />{selectedSupplier.address || '-'}</div>
              </div>
              {isAdmin && (
                <div className="flex gap-2">
                  <Button variant="outline" size="sm" onClick={() => openEdit(selectedSupplier)}><Pencil size={14} className="mr-1" />Edit</Button>
                  <Button variant="destructive" size="sm" onClick={() => { setSelectedSupplier(null); handleDelete(selectedSupplier); }}><Trash2 size={14} className="mr-1" />Delete</Button>
                </div>
              )}
              <div>
                <h3 className="font-semibold text-sm mb-2">Purchases ({(selectedSupplier.purchases || []).length})</h3>
                <div className="table-scroll-wrapper">
                  <Table className="data-table">
                    <TableHeader><TableRow><TableHead>Date</TableHead><TableHead>Type</TableHead><TableHead>Box</TableHead><TableHead>Purity</TableHead><TableHead className="text-right">Weight</TableHead></TableRow></TableHeader>
                    <TableBody>
                      {(selectedSupplier.purchases || []).map((p, i) => (
                        <TableRow key={i}>
                          <TableCell className="text-xs">{formatDateTime(p.created_at)}</TableCell>
                          <TableCell className="text-xs">{p.operation_type}</TableCell>
                          <TableCell className="font-mono text-xs">{p.box_id}</TableCell>
                          <TableCell><span className={`status-badge purity-${p.gold_purity} text-xs`}>{p.gold_purity}</span></TableCell>
                          <TableCell className="text-right weight-value text-xs">{formatWeight(p.gold_weight_g)}</TableCell>
                        </TableRow>
                      ))}
                      {(selectedSupplier.purchases || []).length === 0 && <TableRow><TableCell colSpan={5} className="text-center py-4 text-muted-foreground text-xs">No purchases</TableCell></TableRow>}
                    </TableBody>
                  </Table>
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Edit Dialog */}
      <Dialog open={!!editSupplier} onOpenChange={() => setEditSupplier(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>Edit Karigar: {editSupplier?.name}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div><label className="text-sm font-medium mb-1 block">Name</label><Input value={editForm.name} onChange={e => setEditForm({...editForm, name: e.target.value})} data-testid="karigar-edit-name" /></div>
            <div><label className="text-sm font-medium mb-1 block">Contact</label><Input value={editForm.contact} onChange={e => setEditForm({...editForm, contact: e.target.value})} /></div>
            <div><label className="text-sm font-medium mb-1 block">Address</label><Input value={editForm.address} onChange={e => setEditForm({...editForm, address: e.target.value})} /></div>
            <Button onClick={handleEditSave} className="w-full" data-testid="karigar-edit-save">Save Changes</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
